import type { RiceContent } from "@/pages/bali-info-detail/data/types";
import { img } from "@/lib/images";

/**
 * Grundlage dieses Artikels ist der eigene Text „Reisanbau auf Bali"
 * (vier Absätze, eigene Reihenfolge) mit den eigenen Fotos.
 * Ergänzt wurden nur Abschnitte, die den Ablauf erklären und einordnen.
 */
export const riceContent: RiceContent = {
  intro: {
    de: "Bali zählt neben der Insel Java zu den dicht besiedelten Gebieten dieser Erde. Auf Bali leben auf einer Fläche von 5561 Quadratkilometern fast 4 Millionen Menschen. Das ergibt eine Bevölkerungsdichte von 720 Menschen pro Quadratkilometer. Ein gewaltiger Nahrungsbedarf, der durch Nassreis gedeckt wird. Einzigartig auf Bali ist die Subak-Reisfeldgenossenschaft, welche den gesamten Ablauf des Reisanbaus steuert. Die Felder sind heute alle im Privatbesitz, unterstehen jedoch der Subak. Es gilt das Prinzip des Leben- und Lebenlassens.",
    en: "Bali, along with the island of Java, is one of the most densely populated areas on earth. Almost 4 million people live on an area of 5,561 square kilometers on Bali. This gives a population density of 720 people per square kilometer. A tremendous food requirement, met by wet rice cultivation. Unique to Bali is the Subak rice field cooperative, which controls the entire process of rice cultivation. The fields are all privately owned today, but are subordinate to the Subak. The principle of live and let live applies.",
  },
  introNote: {
    title: { de: "Was die Subak regelt", en: "What the subak regulates" },
    text: {
      de: "Ein Subak umfasst die Felder eines ganzen Wassereinzugsgebietes – oft mehrere Dörfer. Alle Mitglieder treffen sich regelmäßig im Versammlungspavillon, wählen den Vorstand und entscheiden gemeinsam über Wasserzeiten, Reparaturen und Feste. An jeder wichtigen Wasserquelle steht ein Wassertempel, meist der Göttin des Sees und des Wassers geweiht; von dort wird das Wasser über Kanäle und Bambusrohre verteilt. Seit 2012 ist das Subak als UNESCO-Welterbe anerkannt. Sein Fundament ist Tri Hita Karana – das Glück aus der Harmonie zwischen Mensch und Gott, Mensch und Mensch sowie Mensch und Natur.",
      en: "A subak covers the fields of an entire water catchment – often several villages. All members meet regularly in the assembly pavilion, elect the board and decide together on water schedules, repairs and festivals. At every important water source stands a temple, usually dedicated to the goddess of the lake and of water; from there the water is distributed through channels and bamboo pipes. Since 2012 the subak has been a UNESCO World Heritage site. Its foundation is Tri Hita Karana – happiness arising from harmony between people and God, between people, and between people and nature.",
    },
  },
  sections: [
    {
      title: {
        de: "Der Reisanbau im Jahreslauf",
        en: "The rice year",
      },
      icon: "ri-water-flash-line",
      blocks: [
        {
          type: "split",
          image: img("bali-info/about-bali/about-bali-06.jpg"),
          imageAlt: {
            de: "Bali Reisbauer bei der Arbeit",
            en: "Balinese rice farmer at work",
          },
          text: {
            de: "Um den Ablauf einer Pflanzung zu betrachten, beginnen wir nach der Ernte. Ein Feld wird geflutet und dann im nassen, aufgeweichten Zustand gepflügt. Heute findet man auf flachen Feldern schon öfter die „Japanische Kuh“, einen Reisfeldtraktor. Ansonsten stehen Wasserbüffel oder Rinder zur Verfügung. In den Bergen sind die Terrassen sehr schmal, da haben die Bauern keine andere Möglichkeit als den Boden mit einer Hacke umzugraben. Anschließend wird das Feld geglättet, der Lehm fest gepresst.",
            en: "To observe the process of planting, we begin after the harvest. A field is flooded and then plowed in the wet, softened state. Today, on flat fields, one increasingly finds the 'Japanese cow', a rice field tractor. Otherwise, water buffalo or cattle are available. In the mountains, the terraces are very narrow, so the farmers have no choice but to turn the soil with a hoe. Then the field is smoothed, the clay firmly pressed.",
          },
        },
        {
          type: "split",
          image: img("bali-info/about-bali/about-bali-08.jpg"),
          imageAlt: {
            de: "Reispflanzer auf Bali",
            en: "Rice planter on Bali",
          },
          text: {
            de: "Während dieser Zeit wird auf dem Feld ein kleiner Bereich abgegrenzt und darauf Saatgut aufgelegt. Die Körner beginnen zu keimen und es wachsen kleine Pflänzchen heran. Diese Setzlinge werden büschelweise herausgenommen und von Hand eingesteckt, nie mehr als drei bis fünf in einem Abstand von etwa 20 cm. Danach kommt die ruhige Zeit auf dem Reisfeld, einige Jät- und Düngearbeiten sind zu verrichten, bis kurz vor der Ernte. Das Feld wird trocken gelegt und kann nach einigen Wochen abgeerntet werden.",
            en: "During this time, a small area is marked off on the field and seed is placed on it. The grains begin to germinate and small plants grow. These seedlings are removed in bunches and planted by hand, never more than three to five at a distance of about 20 cm. Then comes the quiet time on the rice field, some weeding and fertilizing work must be done until shortly before the harvest. The field is drained and can be harvested after a few weeks.",
          },
        },
        {
          type: "split",
          image: img("bali-info/about-bali/about-bali-07.jpg"),
          imageAlt: {
            de: "Reisernte auf Bali",
            en: "Rice harvest on Bali",
          },
          text: {
            de: "Für die Ernte wird heute in den Niederungen, bei vorwiegendem Hybridenanbau, ein ganzer Bund mit einer Sichel geschnitten und sofort auf dem Feld ausgeschlagen. In den Bergen verwendet man noch immer ein uraltes Instrument für die Reisernte, ein so genanntes Ani-Ani-Messer. Damit wird jede Ähre einzeln geschnitten. Ganz sanft, mit einem in der hohlen Hand versteckten Messer wird jede einzelne Ähre geschnitten. Erstaunlich sind die Erträge. Man spricht in Indonesien bei Hybridreis von 3 bis 4 Ernten in etwa 14 Monaten mit je 6 bis 8 Tonnen pro Hektar.",
            en: "For the harvest, in the lowlands with predominantly hybrid cultivation, an entire bundle is cut with a sickle and immediately threshed on the field. In the mountains, an ancient instrument for the rice harvest is still used, a so-called Ani-Ani knife. With it, each ear is cut individually. Very gently, with a knife hidden in the hollow of the hand, each individual ear is cut. The yields are astonishing. In Indonesia, hybrid rice yields 3 to 4 harvests in about 14 months with 6 to 8 tons per hectare.",
          },
        },
      ],
    },
    {
      title: {
        de: "Reissorten Balis",
        en: "Rice varieties of Bali",
      },
      icon: "ri-seedling-line",
      blocks: [
        {
          type: "text",
          text: {
            de: "Neben dem alltäglichen weißen Reis kennt die balinesische Küche mehrere Sorten, die jeweils eine eigene Aufgabe haben – auf dem Teller und im Tempel.",
            en: "Besides everyday white rice, Balinese cooking knows several varieties, each with its own job – on the plate and in the temple.",
          },
        },
        {
          type: "entries",
          entries: [
            {
              name: { de: "Weißer Reis", en: "White rice" },
              latin: "Beras Putih",
              text: {
                de: "Der Standardreis, gedämpft oder gekocht, Grundlage fast jeder Mahlzeit. Viele Familien kaufen ihn sackweise auf dem Markt; im Dorf wird er oft noch selbst angebaut.",
                en: "The standard rice, steamed or boiled, the base of almost every meal. Many families buy it by the sack at the market, while village households often still grow their own.",
              },
            },
            {
              name: { de: "Roter Reis", en: "Red rice" },
              latin: "Beras Merah",
              text: {
                de: "Vollkornreis mit rötlicher Schale, nussig im Geschmack und etwas fester. Er gilt als traditionelle Sorte und wird gerne in Gesundheitsküchen und Hotels serviert.",
                en: "Wholegrain rice with a reddish husk, nutty in taste and slightly firmer. It is considered a traditional variety and is often served in health kitchens and hotels.",
              },
            },
            {
              name: { de: "Klebreis", en: "Glutinous rice" },
              latin: "Ketan",
              text: {
                de: "Klebrig und süßlich – die Grundlage von Snacks wie Klebreis in Bananenblatt oder den kleinen Reiskuchen, die zu jeder Zeremonie gehören.",
                en: "Sticky and slightly sweet – the base for snacks such as glutinous rice in banana leaf or the small rice cakes that belong to every ceremony.",
              },
            },
            {
              name: { de: "Schwarzer Reis", en: "Black rice" },
              latin: "Beras Hitam",
              text: {
                de: "Tiefschwarz, kräftig im Aroma und lange kochend. Er wird oft mit Kokosmilch zu einem süßen Pudding verarbeitet, der bei Festen auf den Tisch kommt.",
                en: "Deep black, strong in aroma and slow to cook. It is often turned into a sweet coconut-milk pudding served at festivals.",
              },
            },
          ],
        },
      ],
    },
    {
      title: {
        de: "Reis im Alltag",
        en: "Rice in everyday life",
      },
      icon: "ri-restaurant-line",
      blocks: [
        {
          type: "text",
          text: {
            de: "„Hast du schon gegessen?“ – so fragt man auf Bali nach dem Befinden, und gemeint ist immer Reis. Ohne Reis gilt eine Mahlzeit nicht als Mahlzeit.",
            en: "'Have you eaten yet?' – that is how people greet each other on Bali, and rice is always implied. Without rice a meal does not count as a meal.",
          },
        },
        {
          type: "entries",
          entries: [
            {
              name: { de: "Nasi Campur", en: "Nasi campur" },
              text: {
                de: "Ein Teller weißer Reis mit allem, was die Küche hergibt: Gemüse, Tempeh, Ei, Fleisch, Sambal. Die balinesische Variante heißt Nasi Campur Bali und ist deutlich würziger als die übliche Touristenversion.",
                en: "A plate of white rice with whatever the kitchen has: vegetables, tempeh, egg, meat, sambal. The Balinese version is called nasi campur Bali and is considerably spicier than the usual tourist version.",
              },
            },
            {
              name: { de: "Nasi Goreng", en: "Nasi goreng" },
              text: {
                de: "Gebratener Reis – ursprünglich die Verwertung des Reises vom Vortag. Serviert wird er mit Spiegelei, Gurke und Krupuk, gegessen wird er zum Frühstück genauso wie am Abend.",
                en: "Fried rice – originally a way to use up yesterday's rice. It is served with a fried egg, cucumber and crackers, and eaten as readily at breakfast as in the evening.",
              },
            },
            {
              name: { de: "Tumpeng", en: "Tumpeng" },
              text: {
                de: "Der kegelförmig aufgeschichtete Reis ist das Festessen für Geburtstage, Einweihungen und Tempelfeste. Die Form erinnert an den Berg Gunung Agung, den Sitz der Götter.",
                en: "The cone-shaped mound of rice is the festive dish for birthdays, house blessings and temple festivals. Its shape recalls Mount Agung, the seat of the gods.",
              },
            },
            {
              name: { de: "Reis als Opfergabe", en: "Rice as offering" },
              text: {
                de: "In jedem Canang, dem kleinen Opferkörbchen aus Palmblättern, liegen Reiskörner. Auch der rituelle Klebreis gehört zu jeder Zeremonie – Reis wird also nicht nur gegessen, sondern auch verschenkt, geweiht und geteilt.",
                en: "Every canang, the small offering basket of palm leaf, holds grains of rice. Ritual glutinous rice belongs to every ceremony too – rice is not only eaten but also given away, blessed and shared.",
              },
            },
          ],
        },
      ],
    },
  ],
};